<template>
  <div class="products-operations">
    <h3 class="operations-title">Toplu İşlemler</h3>
    <button @click="openImageUploadModal" class="bulk-image-upload">
      <img src="@/assets/images/icons/plus.svg" alt="Toplu Görsel Ekle" />
      Toplu Görsel Ekle
    </button>
    <CurrencyInputLabel
      v-model="bulkOperationsData.listPrice"
      color="unset"
      :options="currencyInputOptions"
      :class="['operations-input', { isErrorTooltip: listPriceErrorMessage }]"
      id="market-selling-price"
      label="Piyasa Satış Fiyatı"
      :disableAutoFocus="true"
      @focus="handleFocusListPrice"
      @blur="handleBlurListPrice"
    >
      <template #right-icon>
        <div v-if="listPriceErrorMessage" class="operations-input-icon">
          <TooltipPlugin :tooltipText="listPriceErrorMessage">
            <template #tooltip-btn>
              <img src="@/assets/images/icons/alert-circle.svg" />
            </template>
          </TooltipPlugin>
        </div>
      </template>
    </CurrencyInputLabel>
    <CurrencyInputLabel
      v-model="bulkOperationsData.salePrice"
      color="unset"
      :options="currencyInputOptions"
      :class="['operations-input', { isErrorTooltip: salePriceErrorMessage }]"
      id="n11-seller-price"
      label="n11 Satış Fiyatı"
      :disableAutoFocus="true"
      @focus="handleFocusSalePrice"
      @blur="handleBlurSalePrice"
    >
      <template #right-icon>
        <div v-if="salePriceErrorMessage" class="operations-input-icon">
          <TooltipPlugin :tooltipText="salePriceErrorMessage">
            <template #tooltip-btn>
              <img src="@/assets/images/icons/alert-circle.svg" />
            </template>
          </TooltipPlugin>
        </div>
      </template>
    </CurrencyInputLabel>
    <div class="input-wrapper">
      <Input
        type="text"
        :isOnlyNumber="true"
        @blur="handleBLurStock"
        :class="['operations-input', { redError: stockErrorMessage }]"
        v-model:value="bulkOperationsData.stock"
        id="stock"
        label="Stok (Adet)"
        :maxLength="3"
        :max="maxStock"
        :style="{ width: '120px' }"
        :inputWidth="'auto'"
      />
      <div v-if="stockErrorMessage" class="basic-input-icon">
        <TooltipPlugin :tooltipText="stockErrorMessage">
          <template #tooltip-btn>
            <img src="@/assets/images/icons/alert-circle.svg" />
          </template>
        </TooltipPlugin>
      </div>
    </div>
    <div class="input-wrapper">
      <Input
        type="number"
        :class="['operations-input', { redError: preparationTimeErrorMessage }]"
        v-model:value="bulkOperationsData.duration"
        @focus="handleFocusPreparationTime"
        @blur="handleBLurPreparationTime"
        id="duration"
        label="Hazırlık Süresi"
        :style="{ width: '120px' }"
        :inputWidth="'auto'"
      />
      <div v-if="preparationTimeErrorMessage" class="basic-input-icon">
        <TooltipPlugin :tooltipText="preparationTimeErrorMessage">
          <template #tooltip-btn>
            <img src="@/assets/images/icons/alert-circle.svg" />
          </template>
        </TooltipPlugin>
      </div>
    </div>
    <NativeSelect
      :options="VAT_RATES"
      :class="['operations-input']"
      v-model:value="bulkOperationsData.vatRate"
      id="vatRate"
      label="KDV Oranı"
      :style="{ width: '106px' }"
    />
    <Button
      primary
      class="applyBtn"
      :disabled="bulkOperationsBtnDisabled"
      @click="$emit('applySelected', true)"
    >
      Seçilenlere Uygula
      <template v-if="selectedRowsLength !== 0">
        ({{ selectedRowsLength }})
      </template>
    </Button>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import {
  Button,
  Input,
  TooltipPlugin,
  NativeSelect,
} from "@n11/so-ui-components";
import CurrencyInputLabel from "@/components/DataTable/CurrencyInputLabel.vue";
import { VAT_RATES } from "@/utils/enum/tableColumns";

const emit = defineEmits(["applySelected", "bulk-image-upload"]);

const props = defineProps({
  selectedRowsLength: {
    type: Number,
    required: false,
    default: 0,
  },
  pid: {
    type: [String, Number],
    required: true,
  },
  categoryId: {
    type: [Number, null],
    required: true,
  },
  selectedCurrency: {
    type: Object,
    default: () => ({}),
  },
});

const openImageUploadModal = () => emit("bulk-image-upload");

const store = useStore();
const maxStock = ref(999);
const bulkOperationsData = ref({
  listPrice: null,
  salePrice: null,
  stock: null,
  duration: null,
  vatRate: null,
});
const preparationTimeErrorMessage = ref(null);
const stockErrorMessage = ref(null);
const listPriceErrorMessage = ref(null);
const salePriceErrorMessage = ref(null);

const currency = computed(() => {
  return {
    rate: props.selectedCurrency.rate,
    name: props.selectedCurrency.name
      ?.split(" ")[1]
      .replace(")", "")
      .replace("(", ""),
    currencyCode: props.selectedCurrency.currencyCode,
  };
});

const currencyInputOptions = computed(() => {
  return {
    locale: "tr-TR",
    currency: props.selectedCurrency.currencyCode,
    currencyDisplay: "symbol",
    hideGroupingSeparatorOnFocus: false,
    hideNegligibleDecimalDigitsOnFocus: false,
    autoDecimalDigits: false,
    useGrouping: true,
    accountingSign: false,
    valueRange: { min: 0 },
  };
});

const bulkOperationsBtnDisabled = computed(() => {
  return (
    !(
      bulkOperationsData.value.listPrice ||
      bulkOperationsData.value.salePrice ||
      bulkOperationsData.value.stock ||
      bulkOperationsData.value.duration ||
      bulkOperationsData.value.vatRate
    ) ||
    props.selectedRowsLength === 0 ||
    (bulkOperationsData.value.listPrice &&
      listPriceErrorMessage.value !== null) ||
    (bulkOperationsData.value.salePrice &&
      salePriceErrorMessage.value !== null) ||
    (bulkOperationsData.value.duration &&
      preparationTimeErrorMessage.value !== null) ||
    (bulkOperationsData.value.stock && stockErrorMessage.value !== null)
  );
});

const getApiResponse = async (field, action, params) => {
  try {
    const res = await store.dispatch(action, params);
    if (res.data.success) {
      field.value = null;
      field.errorMessage.value = null;
    }
  } catch (error) {
    field.errorMessage.value = error.data.message;
  }
};

const handleBLurStock = () => {
  if (!bulkOperationsData.value.stock) stockErrorMessage.value = null;
  return;
};

const handleBLurPreparationTime = async () => {
  if (!bulkOperationsData.value.duration) return;
  const field = { errorMessage: preparationTimeErrorMessage };
  const action = "validations/confirmPreparationTime";
  const params = {
    preparationTime: bulkOperationsData.value.duration,
    categoryId: props.categoryId,
  };
  await getApiResponse(field, action, params);
};

const handleBlurListPrice = async () => {
  if (!bulkOperationsData.value.listPrice) {
    listPriceErrorMessage.value = null;
    return;
  }
  const numericValue =
    parseFloat(bulkOperationsData.value.listPrice) * (currency.value.rate ?? 1);

  if (numericValue <= 0) {
    listPriceErrorMessage.value =
      "Piyasa Satış Fiyatı alanı sıfırdan büyük olmalıdır.";
    return;
  }

  if (
    bulkOperationsData.value.listPrice &&
    bulkOperationsData.value.salePrice &&
    bulkOperationsData.value.listPrice < bulkOperationsData.value.salePrice
  ) {
    listPriceErrorMessage.value =
      "Piyasa Satış Fiyatı, n11 Satış Fiyatı'ndan düşük olamaz.";
    return;
  }
  const field = { errorMessage: listPriceErrorMessage };
  const action = "validations/getPriceConfirmation";
  const params = { categoryId: props.categoryId, price: numericValue };
  await getApiResponse(field, action, params);
};

const handleBlurSalePrice = async () => {
  if (!bulkOperationsData.value.salePrice) {
    salePriceErrorMessage.value = null;
    return;
  }
  const numericValue =
    parseFloat(bulkOperationsData.value.salePrice) * (currency.value.rate ?? 1);
  if (numericValue <= 0 && isNaN(bulkOperationsData.value.salePrice)) return;
  if (numericValue <= 0) {
    salePriceErrorMessage.value =
      "n11 Satış Fiyatı alanı sıfırdan büyük olmalıdır.";
    return;
  }
  if (
    bulkOperationsData.value.listPrice &&
    bulkOperationsData.value.salePrice &&
    bulkOperationsData.value.listPrice < bulkOperationsData.value.salePrice
  ) {
    salePriceErrorMessage.value =
      "n11 Satış Fiyatı Piyasa Satış Fiyatından büyük olamaz.";
    return;
  }
  const field = { errorMessage: salePriceErrorMessage };
  const action = "validations/getPriceConfirmation";
  const params = { categoryId: props.categoryId, price: numericValue };
  await getApiResponse(field, action, params);
};

const handleFocusSalePrice = () => {
  salePriceErrorMessage.value = null;
};
const handleFocusListPrice = () => {
  listPriceErrorMessage.value = null;
};
const handleFocusPreparationTime = () => {
  preparationTimeErrorMessage.value = null;
};

defineExpose({
  bulkOperationsData,
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
.products-operations {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: $colorD10;
  border-radius: 8px;
  padding: 12px;
  position: relative;

  button.bulk-image-upload {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 9px;
    padding: 8px;
    cursor: pointer;
    white-space: nowrap;

    color: $colorM50;
    font-family: "Open Sans";
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
  }

  .operations-title {
    font-size: 16px;
    font-weight: 700;
    white-space: nowrap;
  }

  :deep(.operations-input.redError),
  :deep(.operations-input.isErrorTooltip) {
    border-color: red;
    outline: 3px solid $colorE10;

    &:hover {
      border-color: red;
    }
  }

  :deep(.operations-input) .operations-input-icon {
    width: 18px;
    height: 18px;
    position: absolute;
    z-index: 1;
    right: 12px;
    top: 11px;
    cursor: pointer;
  }

  :deep(.basic-input) {
    scroll-margin: 80px;
  }
  
  :deep(.basic-input.isErrorTooltip) {
    padding-right: 28px !important;
  }

  :deep(.basic-input) .basic-input-icon {
    position: absolute;
    z-index: 1;
    right: 10px;
    top: 11px;
    cursor: pointer;

    img {
      width: 18px;
      height: 18px;
    }
  }

  .input-wrapper {
    position: relative;
  }

  .applyBtn {
    min-width: 171px;
    width: 171px;
    font-size: 14px;
  }
}
</style>
